<template>
  <InputText
    v-model="localValue"
    type="text"
    class="p-column-filter w-100"
    :placeholder="placeholder"
  />
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import InputText from "primevue/inputtext";
import { ref, watch } from "vue";

/**
 * Column filter input that debounces writes into PrimeVue's filter model.
 *
 * PERF: the DataTable deep-watches its filter model and re-runs the whole filter
 * pipeline on every mutation — with a 10k-row catalog that's a visible stutter per
 * keystroke on slower machines. Typing lands here first and reaches the model once,
 * 250ms after the user pauses.
 */

defineProps<{
  placeholder?: string;
}>();

const model = defineModel<string | null>({ required: true });

const localValue = ref(model.value ?? "");
const debouncedValue = refDebounced(localValue, 250);

watch(debouncedValue, (value) => {
  const normalized = value.trim() === "" ? null : value;
  if (normalized !== model.value) model.value = normalized;
});

// External resets (e.g. a cleared filter model) must reflect back into the input.
watch(model, (value) => {
  if ((value ?? "") !== localValue.value) localValue.value = value ?? "";
});
</script>
